import React from 'react';
import {
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';

/**
 * AuditFilters Component
 *
 * Displays filter controls for the audit dashboard.
 *
 * @param {Object} props
 * @param {string} props.searchTerm - Search term value
 * @param {Function} props.onSearchChange - Callback when search changes
 * @param {string} props.referenceTypeFilter - Reference type filter value
 * @param {Function} props.onReferenceTypeChange - Callback when reference type changes
 * @param {string} props.severityFilter - Severity filter value
 * @param {Function} props.onSeverityChange - Callback when severity changes
 * @param {number} props.pageSize - Page size value
 * @param {Function} props.onPageSizeChange - Callback when page size changes
 * @param {Function} props.onExport - Callback for export action
 * @param {Function} props.onRefresh - Callback for refresh action
 */
function AuditFilters({
  searchTerm,
  onSearchChange,
  referenceTypeFilter,
  onReferenceTypeChange,
  severityFilter,
  onSeverityChange,
  pageSize,
  onPageSizeChange,
  onExport,
  onRefresh,
}) {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Content (min. 3 chars)"
              value={searchTerm}
              onChange={e => onSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    position="start"
                    sx={{ position: 'absolute', left: 2 }}
                  >
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': { height: 36 },
                '& .MuiInputBase-input': {
                  padding: '8px 12px',
                  paddingLeft: '16px',
                  fontSize: '12px',
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl
              fullWidth
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { height: 36 } }}
            >
              <InputLabel id="reference-type-label" shrink disableAnimation>
                Reference Type
              </InputLabel>
              <Select
                labelId="reference-type-label"
                value={referenceTypeFilter}
                onChange={e => onReferenceTypeChange(e.target.value)}
                label="Reference Type"
                displayEmpty
                renderValue={selected => {
                  if (
                    selected === '' ||
                    selected === null ||
                    selected === undefined
                  ) {
                    return <span style={{ opacity: 0.6 }}>Reference Type</span>;
                  }
                  const map = {
                    template_validation: 'Template Validation',
                    execution_input: 'Execution Input',
                    execution_anonymization: 'Execution Anonymization',
                    api_call: 'API Call',
                  };
                  return map[selected] || selected;
                }}
                sx={{
                  '.MuiSelect-select': {
                    paddingLeft: '12px',
                    paddingRight: '48px !important',
                    paddingTop: '8px',
                    paddingBottom: '8px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                  '& .MuiSelect-icon': {
                    right: 8,
                    pointerEvents: 'none',
                  },
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="template_validation">
                  Template Validation
                </MenuItem>
                <MenuItem value="execution_input">Execution Input</MenuItem>
                <MenuItem value="execution_anonymization">
                  Execution Anonymization
                </MenuItem>
                <MenuItem value="api_call">API Call</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl
              fullWidth
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { height: 36 } }}
            >
              <InputLabel id="severity-label" shrink disableAnimation>
                Severity
              </InputLabel>
              <Select
                labelId="severity-label"
                value={severityFilter}
                onChange={e => onSeverityChange(e.target.value)}
                label="Severity"
                displayEmpty
                renderValue={selected => {
                  if (
                    selected === '' ||
                    selected === null ||
                    selected === undefined
                  ) {
                    return <span style={{ opacity: 0.6 }}>Severity</span>;
                  }
                  const map = {
                    critical: 'Critical',
                    warning: 'Warning',
                    info: 'Info',
                  };
                  return map[selected] || selected;
                }}
                sx={{
                  '.MuiSelect-select': {
                    paddingLeft: '12px',
                    paddingRight: '48px !important',
                    paddingTop: '8px',
                    paddingBottom: '8px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                  '& .MuiSelect-icon': {
                    right: 8,
                    pointerEvents: 'none',
                  },
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
                <MenuItem value="warning">Warning</MenuItem>
                <MenuItem value="info">Info</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl
              fullWidth
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { height: 36 } }}
            >
              <InputLabel id="page-size-label" shrink disableAnimation>
                Page Size
              </InputLabel>
              <Select
                labelId="page-size-label"
                value={pageSize}
                onChange={e => onPageSizeChange(e.target.value)}
                label="Page Size"
                sx={{
                  '.MuiSelect-select': {
                    paddingLeft: '12px',
                    paddingRight: '48px !important',
                    paddingTop: '8px',
                    paddingBottom: '8px',
                  },
                }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => onExport('csv')}
              >
                Export CSV
              </Button>
              <Button variant="outlined" onClick={onRefresh}>
                Refresh
              </Button>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

export default AuditFilters;
